import { Project, BIMElement, View, Level, Grid, Command, Phase, Sheet, PlacedView, TitleBlock, DesignOption, DesignOptionSet, BIMMaterial, AppearanceAsset, Schedule } from './types';
import { v4 as uuidv4 } from 'uuid';
import { updateBIMWallJoins } from '../utils/WallGeometryUtils';
import { ConstraintSolver } from './ConstraintSolver';
import { createBlankMaterial, createDefaultMaterialLibrary } from './DefaultMaterialLibrary';
import type { MaterialIdentity, MaterialGraphics, MaterialPhysical, MaterialThermal } from './MaterialTypes';

export class BIMKernel {
    private project: Project;
    private undoStack: Command[] = [];
    private redoStack: Command[] = [];

    constructor() {
        this.project = this.createEmptyProject();
        this.initializeFamilies();
    }

    /**
     * Default title block registry (A-series metric + ANSI/Arch imperial).
     * Sizes are in millimeters, matching real paper dimensions.
     */
    static readonly TITLE_BLOCKS: TitleBlock[] = [
        {
            id: 'tb-a0', name: 'A0 Metric', size: 'A0', width: 1189, height: 841,
            fields: [
                { id: 'projectName', label: 'Project Name', value: '' },
                { id: 'projectNumber', label: 'Project Number', value: '' },
                { id: 'client', label: 'Client', value: '' },
                { id: 'address', label: 'Address', value: '' },
                { id: 'sheetName', label: 'Sheet Name', value: '' },
                { id: 'sheetNumber', label: 'Sheet Number', value: '' },
                { id: 'date', label: 'Date', value: '' },
                { id: 'drawnBy', label: 'Drawn By', value: '' },
                { id: 'checkedBy', label: 'Checked By', value: '' },
                { id: 'scale', label: 'Scale', value: '' },
            ]
        },
        {
            id: 'tb-a1', name: 'A1 Metric', size: 'A1', width: 841, height: 594,
            fields: [
                { id: 'projectName', label: 'Project Name', value: '' },
                { id: 'projectNumber', label: 'Project Number', value: '' },
                { id: 'sheetName', label: 'Sheet Name', value: '' },
                { id: 'sheetNumber', label: 'Sheet Number', value: '' },
                { id: 'date', label: 'Date', value: '' },
                { id: 'drawnBy', label: 'Drawn By', value: '' },
                { id: 'scale', label: 'Scale', value: '' },
            ]
        },
        {
            id: 'tb-a2', name: 'A2 Metric', size: 'A2', width: 594, height: 420,
            fields: [
                { id: 'projectName', label: 'Project Name', value: '' },
                { id: 'projectNumber', label: 'Project Number', value: '' },
                { id: 'sheetName', label: 'Sheet Name', value: '' },
                { id: 'sheetNumber', label: 'Sheet Number', value: '' },
                { id: 'date', label: 'Date', value: '' },
                { id: 'scale', label: 'Scale', value: '' },
            ]
        },
        {
            id: 'tb-a3', name: 'A3 Metric', size: 'A3', width: 420, height: 297,
            fields: [
                { id: 'projectName', label: 'Project Name', value: '' },
                { id: 'sheetName', label: 'Sheet Name', value: '' },
                { id: 'sheetNumber', label: 'Sheet Number', value: '' },
                { id: 'date', label: 'Date', value: '' },
                { id: 'scale', label: 'Scale', value: '' },
            ]
        },
        {
            id: 'tb-a4', name: 'A4 Metric', size: 'A4', width: 297, height: 210,
            fields: [
                { id: 'projectName', label: 'Project Name', value: '' },
                { id: 'sheetName', label: 'Sheet Name', value: '' },
                { id: 'sheetNumber', label: 'Sheet Number', value: '' },
                { id: 'scale', label: 'Scale', value: '' },
            ]
        },
        {
            id: 'tb-24x36', name: 'Arch D - 24" x 36"', size: 'ARCH_D', width: 914.4, height: 609.6,
            fields: [
                { id: 'projectName', label: 'Project Name', value: '' },
                { id: 'projectNumber', label: 'Project Number', value: '' },
                { id: 'sheetName', label: 'Sheet Name', value: '' },
                { id: 'sheetNumber', label: 'Sheet Number', value: '' },
                { id: 'date', label: 'Date', value: '' },
                { id: 'drawnBy', label: 'Drawn By', value: '' },
                { id: 'scale', label: 'Scale', value: '' },
            ]
        },
        {
            id: 'tb-30x42', name: 'Arch E1 - 30" x 42"', size: 'ARCH_E', width: 1066.8, height: 762,
            fields: [
                { id: 'projectName', label: 'Project Name', value: '' },
                { id: 'projectNumber', label: 'Project Number', value: '' },
                { id: 'client', label: 'Client', value: '' },
                { id: 'sheetName', label: 'Sheet Name', value: '' },
                { id: 'sheetNumber', label: 'Sheet Number', value: '' },
                { id: 'date', label: 'Date', value: '' },
                { id: 'drawnBy', label: 'Drawn By', value: '' },
                { id: 'checkedBy', label: 'Checked By', value: '' },
                { id: 'scale', label: 'Scale', value: '' },
            ]
        },
        {
            id: 'tb-ansi-b', name: 'ANSI B - 11" x 17"', size: 'ANSI_B', width: 431.8, height: 279.4,
            fields: [
                { id: 'projectName', label: 'Project Name', value: '' },
                { id: 'sheetName', label: 'Sheet Name', value: '' },
                { id: 'sheetNumber', label: 'Sheet Number', value: '' },
                { id: 'scale', label: 'Scale', value: '' },
            ]
        },
        {
            id: 'tb-ansi-d', name: 'ANSI D - 22" x 34"', size: 'ANSI_D', width: 558.8, height: 863.6,
            fields: [
                { id: 'projectName', label: 'Project Name', value: '' },
                { id: 'projectNumber', label: 'Project Number', value: '' },
                { id: 'sheetName', label: 'Sheet Name', value: '' },
                { id: 'sheetNumber', label: 'Sheet Number', value: '' },
                { id: 'date', label: 'Date', value: '' },
                { id: 'scale', label: 'Scale', value: '' },
            ]
        },
        {
            id: 'tb-ansi-e', name: 'ANSI E - 34" x 44"', size: 'ANSI_E', width: 863.6, height: 1117.6,
            fields: [
                { id: 'projectName', label: 'Project Name', value: '' },
                { id: 'projectNumber', label: 'Project Number', value: '' },
                { id: 'sheetName', label: 'Sheet Name', value: '' },
                { id: 'sheetNumber', label: 'Sheet Number', value: '' },
                { id: 'date', label: 'Date', value: '' },
                { id: 'drawnBy', label: 'Drawn By', value: '' },
                { id: 'scale', label: 'Scale', value: '' },
            ]
        },
    ];

    private initializeFamilies() {
        // Sync family manager with project
        // In a real app, we might load project-specific families here
        // const allTypes = familyManager.getAllTypes();
        // For now, we don't strictly enforce that project.families matches familyManager
        // but we could populate project.families here if we wanted to serialize them.
    }

    private createEmptyProject(): Project {
        const level1Id = uuidv4();
        const level2Id = uuidv4();
        const defaultViewId = uuidv4();
        const view3DId = uuidv4();
        const phase1Id = uuidv4();
        const seedLib = createDefaultMaterialLibrary();

        const levels = new Map<string, Level>();
        levels.set(level1Id, { id: level1Id, name: 'Level 1', elevation: 0, isStory: true });
        levels.set(level2Id, { id: level2Id, name: 'Level 2', elevation: 3000, isStory: true }); // mm

        const defaultView: View = {
            id: defaultViewId,
            name: 'Level 1',
            type: 'FloorPlan',
            scale: 100,
            detailLevel: 'Coarse',
            visibility: {},
            filters: [],
            associatedLevelId: level1Id,
            viewRange: {
                top: 4.0,      // 4 m above level (mm units)
                cutPlane: 1.2, // cut plane at 1.2 m above level
                bottom: 0,
                depth: 1.0     // show elements 1 m below the level
            },
            camera: {
                position: [0, 0, 100],
                target: [0, 0, 0],
                projection: 'Orthographic'
            }
        };

        const view3D: View = {
            id: view3DId,
            name: '{3D}',
            type: '3D',
            scale: 1,
            detailLevel: 'Medium',
            visibility: {},
            filters: [],
            camera: {
                position: [20, 20, 20],
                target: [0, 0, 0],
                projection: 'Perspective'
            }
        };

        const drawingViewId = uuidv4();
        const drawingView: View = {
            id: drawingViewId,
            name: 'Drawing1',
            type: 'Drawing',
            scale: 100,
            detailLevel: 'Coarse',
            visibility: {},
            filters: [],
            camera: {
                position: [0, 0, 100],
                target: [0, 0, 0],
                projection: 'Orthographic'
            }
        };

        const views = new Map<string, View>();
        views.set(defaultViewId, defaultView);
        views.set(view3DId, view3D);
        views.set(drawingViewId, drawingView);

        const phases: Phase[] = [
            { id: phase1Id, name: 'New Construction', sequence: 1 }
        ];

        return {
            id: uuidv4(),
            name: 'Project 1',
            number: '001',
            client: '',
            address: '',
            nextElementId: 1,
            elements: new Map(),
            views: views,
            sheets: new Map(),
            schedules: new Map(),
            families: new Map(),
            materials: new Map(seedLib.materials.map(m => [m.id, m])),
            appearanceAssets: new Map(seedLib.assets.map(a => [a.id, a])),
            // initialize global parameters map
            globalParameters: new Map<string, any>(),
            levels: levels,
            grids: new Map(),
            phases: phases,
            designOptionSets: [],
            revisions: [],
            customTitleBlocks: [],
            activeViewId: defaultViewId,
            activeSheetId: null,
            activePhaseId: phase1Id
        };
    }

    getProject(): Project {
        return this.project;
    }

    /**
     * Replaces the in-memory document with a fresh empty project — the
     * kernel-side half of "New Project" / "New Project Template" (BIM
     * derives every new document from a template; see ProjectTemplateManager).
     * Discards the undo/redo history of the previous document.
     */
    newDocument(name?: string): Project {
        this.project = this.createEmptyProject();
        if (name) this.project.name = name;
        this.undoStack = [];
        this.redoStack = [];
        return this.project;
    }

    // --- Element Management ---
    addElement(element: BIMElement): void {
        const cmd: Command = {
            id: uuidv4(),
            type: 'ADD_ELEMENT',
            timestamp: Date.now(),
            description: `Add ${element.category}: ${element.type}`,
            execute: () => {
                // Replace-on-write: rebuild the map identity so downstream
                // subscribers (zustand selectors / React.memo) observe changes.
                const next = new Map(this.project.elements);
                next.set(element.id, element);
                this.project.elements = next;
                this.updateConstraints();

                // Handle Wall Joins
                if (element.category === 'Walls') {
                    const updates = updateBIMWallJoins(this.project.elements);
                    if (updates.size > 0) {
                        const joined = new Map(this.project.elements);
                        updates.forEach((update, id) => {
                            const el = joined.get(id);
                            if (el) joined.set(id, { ...el, ...update });
                        });
                        this.project.elements = joined;
                    }
                }
            },
            undo: () => {
                const next = new Map(this.project.elements);
                next.delete(element.id);
                this.project.elements = next;
                // Re-eval constraints of neighbors might be needed here
                // Also undo wall joins? Ideally yes, but complex.
                // For now, we rely on the fact that removing the element will leave others as is (mitered to nothing?)
                // Actually, if we remove a wall, the neighbor should revert to straight.
                // To fix this properly, we should re-run join logic on neighbors after removal.

                // Find neighbors
                // This is tricky without the element in the map.
                // We can temporarily add it back, find neighbors, then remove?
                // Or just re-run joins for all walls? Too slow.
            }
        };
        this.executeCommand(cmd);
    }

    removeElement(id: string): void {
        const element = this.project.elements.get(id);
        if (!element) return;

        const cmd: Command = {
            id: uuidv4(),
            type: 'REMOVE_ELEMENT',
            timestamp: Date.now(),
            description: `Delete ${element.category}`,
            execute: () => {
                const next = new Map(this.project.elements);
                next.delete(id);
                this.project.elements = next;
                // TODO: Update neighbors' joins
            },
            undo: () => {
                const next = new Map(this.project.elements);
                next.set(id, element);
                this.project.elements = next;
                // TODO: Restore joins
            }
        };
        this.executeCommand(cmd);
    }

    getElement(id: string): BIMElement | undefined {
        return this.project.elements.get(id);
    }

    updateElement(id: string, updates: Partial<BIMElement>): void {
        const el = this.getElement(id);
        if (el) {
            const oldState = structuredClone(el); // Deep copy for undo
            const cmd: Command = {
                id: uuidv4(),
                type: 'UPDATE_ELEMENT',
                timestamp: Date.now(),
                description: `Modify ${el.category}`,
                execute: () => {
                    // Replace-on-write: fresh object identity for this element only
                    this.setElementObject(id, { ...el, ...updates } as BIMElement);

                    // Solve Constraints. `el` is never mutated (replace-on-write),
                    // so oldState already holds the exact pre-update content on
                    // every (re)execution — no need to re-clone it here. solve()
                    // only reads the snapshot, so it stays pristine for undo.
                    const sideEffects = ConstraintSolver.solve(this.project, id, oldState);
                    this.applyUpdates(sideEffects);
                },
                undo: () => {
                    const current = this.project.elements.get(id);
                    const currentState = current ? structuredClone(current) : null;
                    this.setElementObject(id, { ...oldState } as BIMElement);

                    // Solve Constraints (Reverse side effects)
                    if (currentState) {
                        const sideEffects = ConstraintSolver.solve(this.project, id, currentState);
                        this.applyUpdates(sideEffects);
                    }
                }
            };
            this.executeCommand(cmd);
        }
    }

    /**
     * Replace-on-write helper (per-element subscription enabler).
     *
     * Element updates must produce a NEW element object and a NEW elements Map
     * identity so per-element store subscribers and memoized renderers can rely
     * on reference equality, while untouched elements stay referentially stable
     * (structural sharing semantics, as assumed by plan task 2.2).
     */
    private setElementObject(id: string, element: BIMElement): void {
        const next = new Map(this.project.elements);
        next.set(id, element);
        this.project.elements = next;
    }

    /**
     * Batch element update as a single undoable command.
     *
     * Multi-element operations (BIM-style wall moves that stretch joined
     * walls) must commit as ONE command: per-element updateElement calls
     * would push N undo entries, run the constraint solver N times and
     * trigger N full store refreshes.
     */
    updateElements(batch: Array<{ id: string; updates: Partial<BIMElement> }>): void {
        const valid = batch.filter(b => this.project.elements.has(b.id));
        if (valid.length === 0) return;

        const oldStates = valid.map(b => ({
            id: b.id,
            state: structuredClone(this.project.elements.get(b.id)!),
        }));

        const cmd: Command = {
            id: uuidv4(),
            type: 'UPDATE_ELEMENTS',
            timestamp: Date.now(),
            description: `Modify ${valid.length} element${valid.length === 1 ? '' : 's'}`,
            execute: () => {
                const next = new Map(this.project.elements);
                for (const b of valid) {
                    const el = next.get(b.id);
                    if (el) next.set(b.id, { ...el, ...b.updates } as BIMElement);
                }
                this.project.elements = next;
            },
            undo: () => {
                const next = new Map(this.project.elements);
                for (const old of oldStates) {
                    next.set(old.id, old.state as BIMElement);
                }
                this.project.elements = next;
            }
        };
        this.executeCommand(cmd);
    }

    private applyUpdates(updates: Map<string, Partial<BIMElement>>) {
        if (updates.size === 0) return;
        const next = new Map(this.project.elements);
        updates.forEach((update, id) => {
            const el = next.get(id);
            if (el) next.set(id, { ...el, ...update } as BIMElement);
        });
        this.project.elements = next;
    }

    /**
     * Run one multi-element edit (remove + add) as a SINGLE undoable command.
     *
     * The Precast split/reinforcement workflows replace a monolithic host with
     * a generated panel set; per-element removeElement/addElement calls would
     * push N undo entries and leave the model half-restored after one Ctrl+Z.
     */
    replaceElements(description: string, removeIds: string[], addElements: BIMElement[]): void {
        const oldElements = removeIds
            .map(id => this.project.elements.get(id))
            .filter((el): el is BIMElement => !!el);

        const cmd: Command = {
            id: uuidv4(),
            type: 'REPLACE_ELEMENTS',
            timestamp: Date.now(),
            description,
            execute: () => {
                const next = new Map(this.project.elements);
                for (const id of removeIds) next.delete(id);
                for (const el of addElements) next.set(el.id, el);
                this.project.elements = next;
            },
            undo: () => {
                const next = new Map(this.project.elements);
                for (const el of addElements) next.delete(el.id);
                for (const el of oldElements) next.set(el.id, el);
                this.project.elements = next;
            }
        };
        this.executeCommand(cmd);
    }

    /**
     * Edit Type commit: apply a family-type mutation (parameter bag rewrite,
     * rename, re-key) AND all derived instance updates as ONE undoable
     * command. The type registry lives outside the project, so its revert is
     * supplied as closures by the caller while element states are snapshotted
     * here like updateElements does.
     */
    applyTypeChange(opts: {
        description: string;
        applyType: () => void;
        revertType: () => void;
        elementBatch: Array<{ id: string; updates: Partial<BIMElement> }>;
    }): void {
        const valid = opts.elementBatch.filter(b => this.project.elements.has(b.id));

        const cmd: Command = {
            id: uuidv4(),
            type: 'APPLY_TYPE_CHANGE',
            timestamp: Date.now(),
            description: opts.description,
            execute: () => {
                const oldStates = valid.map(b => ({
                    id: b.id,
                    state: structuredClone(this.project.elements.get(b.id)!),
                }));
                opts.applyType();
                const next = new Map(this.project.elements);
                for (const b of valid) {
                    const el = next.get(b.id);
                    if (el) next.set(b.id, { ...el, ...b.updates } as BIMElement);
                }
                this.project.elements = next;
                // Stash for undo (execute may run again on redo; recompute then)
                (cmd as any).__oldStates = oldStates;
            },
            undo: () => {
                const oldStates = (cmd as any).__oldStates as Array<{ id: string; state: BIMElement }>;
                const next = new Map(this.project.elements);
                for (const old of oldStates) {
                    next.set(old.id, { ...old.state });
                }
                this.project.elements = next;
                opts.revertType();
            }
        };
        this.executeCommand(cmd);
    }

    // Parametric Engine - Deprecated, replaced by ConstraintSolver
    private updateConstraints(): void {
        // Kept for compatibility if needed, but logic moved to ConstraintSolver
    }

    // --- View Management ---
    createView(name: string, type: View['type']): View {
        const view: View = {
            id: uuidv4(),
            name,
            type,
            scale: 100,
            detailLevel: 'Coarse',
            visibility: {},
            filters: [],
            associatedLevelId: undefined
        };
        this.project.views.set(view.id, view);
        return view;
    }

    setActiveView(viewId: string): void {
        if (this.project.views.has(viewId)) {
            this.project.activeViewId = viewId;
            // Opening a model view always closes the sheet
            this.project.activeSheetId = null;
        }
    }

    /** Register a fully-built view (elevation/section/callout tools). */
    addView(view: View): void {
        const next = new Map(this.project.views);
        next.set(view.id, view);
        this.project.views = next;
    }

    /** Delete a view and leave the marker element in the model untouched. */
    removeView(viewId: string): void {
        if (!this.project.views.has(viewId)) return;
        const next = new Map(this.project.views);
        next.delete(viewId);
        this.project.views = next;
        if (this.project.activeViewId === viewId) {
            const first = next.values().next().value;
            this.project.activeViewId = first ? first.id : this.project.activeViewId;
        }
    }

    // --- Schedule Management ---
    createSchedule(schedule: Schedule): void {
        const next = new Map(this.project.schedules);
        next.set(schedule.id, schedule);
        this.project.schedules = next;
    }

    removeSchedule(scheduleId: string): void {
        const next = new Map(this.project.schedules);
        next.delete(scheduleId);
        this.project.schedules = next;
    }

    // --- Sheet Management ---
    getTitleBlock(titleBlockId: string): TitleBlock | undefined {
        return BIMKernel.TITLE_BLOCKS.find(tb => tb.id === titleBlockId)
            ?? this.project.customTitleBlocks.find(tb => tb.id === titleBlockId);
    }

    getAllTitleBlocks(): TitleBlock[] {
        return [...BIMKernel.TITLE_BLOCKS, ...this.project.customTitleBlocks];
    }

    /**
     * Create a user-defined paper type (BIM: New Title Block).
     * Persisted on the project so it survives save/load.
     */
    createCustomTitleBlock(name: string, widthMm: number, heightMm: number): TitleBlock {
        const tb: TitleBlock = {
            id: `tb-custom-${uuidv4()}`,
            name,
            size: 'A1',
            width: widthMm,
            height: heightMm,
            fields: [
                { id: 'projectName', label: 'Project Name', value: '' },
                { id: 'projectNumber', label: 'Project Number', value: '' },
                { id: 'sheetName', label: 'Sheet Name', value: '' },
                { id: 'sheetNumber', label: 'Sheet Number', value: '' },
                { id: 'date', label: 'Date', value: '' },
                { id: 'scale', label: 'Scale', value: '' },
            ]
        };

        const cmd: Command = {
            id: uuidv4(),
            type: 'ADD_TITLE_BLOCK',
            timestamp: Date.now(),
            description: `New Paper Type: ${name}`,
            execute: () => {
                this.project.customTitleBlocks = [...this.project.customTitleBlocks, tb];
            },
            undo: () => {
                this.project.customTitleBlocks = this.project.customTitleBlocks.filter(t => t.id !== tb.id);
            }
        };
        this.executeCommand(cmd);
        return tb;
    }

    createSheet(number: string, name: string, titleBlockId: string, style?: string): Sheet {
        const sheet: Sheet = {
            id: uuidv4(),
            number,
            name,
            titleBlockId,
            viewsPlaced: [],
            revisions: [],
            style,
        };

        const cmd: Command = {
            id: uuidv4(),
            type: 'ADD_SHEET',
            timestamp: Date.now(),
            description: `Add Sheet: ${number} - ${name}`,
            execute: () => {
                // Replace-on-write: fresh map identity for subscribers
                const next = new Map(this.project.sheets);
                next.set(sheet.id, sheet);
                this.project.sheets = next;
            },
            undo: () => {
                const next = new Map(this.project.sheets);
                next.delete(sheet.id);
                this.project.sheets = next;
            }
        };
        this.executeCommand(cmd);
        return sheet;
    }

    removeSheet(sheetId: string): void {
        const sheet = this.project.sheets.get(sheetId);
        if (!sheet) return;

        const cmd: Command = {
            id: uuidv4(),
            type: 'REMOVE_SHEET',
            timestamp: Date.now(),
            description: `Delete Sheet: ${sheet.number}`,
            execute: () => {
                const next = new Map(this.project.sheets);
                next.delete(sheetId);
                this.project.sheets = next;
                if (this.project.activeSheetId === sheetId) {
                    this.project.activeSheetId = null;
                }
            },
            undo: () => {
                const next = new Map(this.project.sheets);
                next.set(sheetId, sheet);
                this.project.sheets = next;
            }
        };
        this.executeCommand(cmd);
    }

    updateSheet(sheetId: string, updates: Partial<Sheet>): void {
        const sheet = this.project.sheets.get(sheetId);
        if (!sheet) return;

        const oldState = structuredClone(sheet);
        const cmd: Command = {
            id: uuidv4(),
            type: 'UPDATE_SHEET',
            timestamp: Date.now(),
            description: `Modify Sheet: ${sheet.number}`,
            execute: () => {
                const next = new Map(this.project.sheets);
                next.set(sheetId, { ...sheet, ...updates });
                this.project.sheets = next;
            },
            undo: () => {
                const next = new Map(this.project.sheets);
                next.set(sheetId, oldState);
                this.project.sheets = next;
            }
        };
        this.executeCommand(cmd);
    }

    setActiveSheet(sheetId: string | null): void {
        if (sheetId !== null && !this.project.sheets.has(sheetId)) return;
        this.project.activeSheetId = sheetId;
    }

    placeViewOnSheet(sheetId: string, placedView: PlacedView): void {
        const sheet = this.project.sheets.get(sheetId);
        if (!sheet) return;

        const cmd: Command = {
            id: uuidv4(),
            type: 'PLACE_VIEW_ON_SHEET',
            timestamp: Date.now(),
            description: `Place View on Sheet: ${sheet.number}`,
            execute: () => {
                const next = new Map(this.project.sheets);
                next.set(sheetId, {
                    ...sheet,
                    viewsPlaced: [...sheet.viewsPlaced, placedView]
                });
                this.project.sheets = next;
            },
            undo: () => {
                const next = new Map(this.project.sheets);
                next.set(sheetId, {
                    ...sheet,
                    viewsPlaced: sheet.viewsPlaced.filter(pv => pv.id !== placedView.id)
                });
                this.project.sheets = next;
            }
        };
        this.executeCommand(cmd);
    }

    removePlacedView(sheetId: string, placedViewId: string): void {
        const sheet = this.project.sheets.get(sheetId);
        if (!sheet) return;
        const removed = sheet.viewsPlaced.find(pv => pv.id === placedViewId);
        if (!removed) return;

        const cmd: Command = {
            id: uuidv4(),
            type: 'REMOVE_PLACED_VIEW',
            timestamp: Date.now(),
            description: `Remove Viewport from Sheet: ${sheet.number}`,
            execute: () => {
                const next = new Map(this.project.sheets);
                next.set(sheetId, {
                    ...sheet,
                    viewsPlaced: sheet.viewsPlaced.filter(pv => pv.id !== placedViewId)
                });
                this.project.sheets = next;
            },
            undo: () => {
                const next = new Map(this.project.sheets);
                next.set(sheetId, {
                    ...sheet,
                    viewsPlaced: [...sheet.viewsPlaced, removed]
                });
                this.project.sheets = next;
            }
        };
        this.executeCommand(cmd);
    }

    movePlacedView(sheetId: string, placedViewId: string, position: { x: number; y: number }): void {
        const sheet = this.project.sheets.get(sheetId);
        if (!sheet) return;
        const placed = sheet.viewsPlaced.find(pv => pv.id === placedViewId);
        if (!placed) return;

        // Dragging is a continuous operation: commit as a direct mutation with
        // replace-on-write identity (not undoable per-pixel, like BIM drag).
        const next = new Map(this.project.sheets);
        next.set(sheetId, {
            ...sheet,
            viewsPlaced: sheet.viewsPlaced.map(pv =>
                pv.id === placedViewId ? { ...pv, position } : pv
            )
        });
        this.project.sheets = next;
    }

    updateView(viewId: string, updates: Partial<View>): void {        const view = this.project.views.get(viewId);
        if (view) {
            const oldState = { ...view };
            const cmd: Command = {
                id: uuidv4(),
                type: 'UPDATE_VIEW',
                timestamp: Date.now(),
                description: `Modify View: ${view.name}`,
                execute: () => {
                    // Replace-on-write: fresh view identity for subscribers
                    this.project.views.set(viewId, { ...view, ...updates });
                },
                undo: () => {
                    this.project.views.set(viewId, { ...oldState });
                }
            };
            this.executeCommand(cmd);
        }
    }

    // --- Level Management ---
    addLevel(name: string, elevation: number): Level {
        const level: Level = { id: uuidv4(), name, elevation, isStory: true };

        const cmd: Command = {
            id: uuidv4(),
            type: 'ADD_LEVEL',
            timestamp: Date.now(),
            description: `Add Level: ${name}`,
            execute: () => {
                this.project.levels.set(level.id, level);
                // Auto-create view
                const view: View = {
                    id: uuidv4(),
                    name: name,
                    type: 'FloorPlan',
                    scale: 100,
                    detailLevel: 'Coarse',
                    visibility: {},
                    filters: [],
                    associatedLevelId: level.id,
                    viewRange: {
                        top: 4.0,
                        cutPlane: 1.2,
                        bottom: 0,
                        depth: 1.0
                    },
                    camera: {
                        position: [0, 0, elevation + 50],
                        target: [0, 0, elevation],
                        projection: 'Orthographic'
                    }
                };
                this.project.views.set(view.id, view);
            },
            undo: () => {
                this.project.levels.delete(level.id);
                // Remove associated view (simplified)
            }
        };
        this.executeCommand(cmd);

        return level;
    }

    updateLevel(id: string, updates: Partial<Level>): void {
        const level = this.project.levels.get(id);
        if (level) {
            const oldState = { ...level };
            const cmd: Command = {
                id: uuidv4(),
                type: 'UPDATE_LEVEL',
                timestamp: Date.now(),
                description: `Modify Level: ${level.name}`,
                execute: () => {
                    // Replace-on-write: fresh level identity for subscribers
                    this.project.levels.set(id, { ...level, ...updates });
                    this.updateConstraints();
                },
                undo: () => {
                    this.project.levels.set(id, { ...oldState });
                    this.updateConstraints();
                }
            };
            this.executeCommand(cmd);
        }
    }

    // --- Grid Management ---
    addGrid(name: string, start: [number, number], end: [number, number]): Grid {
        // extents3D captures the creation-time span so the context tab's
        // "Reset to 3D Extents" can restore it after 2D edits.
        const grid: Grid = { id: uuidv4(), name, start, end, bubbleEnd: 'End', extents3D: { start, end } };
        const cmd: Command = {
            id: uuidv4(),
            type: 'ADD_GRID',
            timestamp: Date.now(),
            description: `Add Grid: ${name}`,
            execute: () => {
                this.project.grids.set(grid.id, grid);
            },
            undo: () => {
                this.project.grids.delete(grid.id);
            }
        };
        this.executeCommand(cmd);
        return grid;
    }

    updateGrid(id: string, updates: Partial<Grid>): void {
        const grid = this.project.grids.get(id);
        if (grid) {
            const oldState = { ...grid };
            const cmd: Command = {
                id: uuidv4(),
                type: 'UPDATE_GRID',
                timestamp: Date.now(),
                description: `Modify Grid: ${grid.name}`,
                execute: () => {
                    // Replace-on-write: fresh grid identity for subscribers
                    this.project.grids.set(id, { ...grid, ...updates });
                },
                undo: () => {
                    this.project.grids.set(id, { ...oldState });
                }
            };
            this.executeCommand(cmd);
        }
    }

    // --- Undo/Redo ---
    private executeCommand(cmd: Command): void {
        cmd.execute();
        this.undoStack.push(cmd);
        this.redoStack = []; // Clear redo stack on new action
    }

    undo(): void {
        const cmd = this.undoStack.pop();
        if (cmd) {
            cmd.undo();
            this.redoStack.push(cmd);
        }
    }

    redo(): void {
        const cmd = this.redoStack.pop();
        if (cmd) {
            cmd.execute();
            this.undoStack.push(cmd);
        }
    }

    canUndo(): boolean {
        return this.undoStack.length > 0;
    }

    canRedo(): boolean {
        return this.redoStack.length > 0;
    }

    // --- Project Info (Manage > Location) ---
    updateProjectInfo(updates: Partial<Pick<Project, 'name' | 'number' | 'client' | 'address'>>): void {
        const oldState: Partial<Project> = {
            name: this.project.name,
            number: this.project.number,
            client: this.project.client,
            address: this.project.address
        };
        const cmd: Command = {
            id: uuidv4(),
            type: 'UPDATE_PROJECT_INFO',
            timestamp: Date.now(),
            description: 'Update Project Information',
            execute: () => {
                this.project = { ...this.project, ...updates };
            },
            undo: () => {
                this.project = { ...this.project, ...oldState };
            }
        };
        this.executeCommand(cmd);
    }

    // --- Phase Management (Manage > Phasing) ---
    addPhase(name: string): Phase {
        const phase: Phase = {
            id: uuidv4(),
            name,
            sequence: this.project.phases.length + 1
        };
        const cmd: Command = {
            id: uuidv4(),
            type: 'ADD_PHASE',
            timestamp: Date.now(),
            description: `Add Phase: ${name}`,
            execute: () => {
                this.project.phases = [...this.project.phases, phase];
            },
            undo: () => {
                this.project.phases = this.project.phases.filter(p => p.id !== phase.id);
            }
        };
        this.executeCommand(cmd);
        return phase;
    }

    renamePhase(id: string, name: string): void {
        const phase = this.project.phases.find(p => p.id === id);
        if (!phase) return;
        const oldName = phase.name;
        const cmd: Command = {
            id: uuidv4(),
            type: 'RENAME_PHASE',
            timestamp: Date.now(),
            description: `Rename Phase: ${oldName} -> ${name}`,
            execute: () => {
                this.project.phases = this.project.phases.map(p => (p.id === id ? { ...p, name } : p));
            },
            undo: () => {
                this.project.phases = this.project.phases.map(p => (p.id === id ? { ...p, name: oldName } : p));
            }
        };
        this.executeCommand(cmd);
    }

    removePhase(id: string): boolean {
        const phase = this.project.phases.find(p => p.id === id);
        if (!phase || this.project.phases.length <= 1) return false;

        const oldPhases = [...this.project.phases];
        const oldActivePhaseId = this.project.activePhaseId;
        const affected = Array.from(this.project.elements.values())
            .filter(el => el.phaseCreatedId === id)
            .map(el => el.id);

        const cmd: Command = {
            id: uuidv4(),
            type: 'REMOVE_PHASE',
            timestamp: Date.now(),
            description: `Remove Phase: ${phase.name}`,
            execute: () => {
                this.project.phases = this.project.phases.filter(p => p.id !== id);
                // Re-phase orphaned elements into the remaining first phase
                const fallback = this.project.phases[0];
                if (fallback) {
                    const next = new Map(this.project.elements);
                    affected.forEach(elId => {
                        const el = next.get(elId);
                        if (el) next.set(elId, { ...el, phaseCreatedId: fallback.id });
                    });
                    this.project.elements = next;
                }
                if (this.project.activePhaseId === id) {
                    this.project.activePhaseId = fallback?.id ?? '';
                }
            },
            undo: () => {
                this.project.phases = oldPhases;
                this.project.activePhaseId = oldActivePhaseId;
            }
        };
        this.executeCommand(cmd);
        return true;
    }

    setActivePhase(id: string): void {
        if (!this.project.phases.some(p => p.id === id)) return;
        this.project = { ...this.project, activePhaseId: id };
    }

    /** Assign phaseCreatedId on the given elements (Manage > Phasing > Assign to Selection) */
    assignPhaseToElements(elementIds: string[], phaseId: string): void {
        const phase = this.project.phases.find(p => p.id === phaseId);
        if (!phase || elementIds.length === 0) return;

        const oldStates = new Map<string, string | undefined>();
        elementIds.forEach(elId => {
            const el = this.project.elements.get(elId);
            if (el) oldStates.set(elId, el.phaseCreatedId);
        });
        if (oldStates.size === 0) return;

        const cmd: Command = {
            id: uuidv4(),
            type: 'ASSIGN_PHASE',
            timestamp: Date.now(),
            description: `Assign ${elementIds.length} element(s) to Phase: ${phase.name}`,
            execute: () => {
                const next = new Map(this.project.elements);
                oldStates.forEach((_, elId) => {
                    const el = next.get(elId);
                    if (el) next.set(elId, { ...el, phaseCreatedId: phaseId });
                });
                this.project.elements = next;
            },
            undo: () => {
                const next = new Map(this.project.elements);
                oldStates.forEach((oldValue, elId) => {
                    const el = next.get(elId);
                    if (el) next.set(elId, { ...el, phaseCreatedId: oldValue });
                });
                this.project.elements = next;
            }
        };
        this.executeCommand(cmd);
    }

    // --- Design Option Management (Manage > Design Options) ---
    addDesignOptionSet(name: string): DesignOptionSet {
        const set: DesignOptionSet = {
            id: uuidv4(),
            name,
            options: [{ id: uuidv4(), name: 'Option 1', isPrimary: true }],
        };
        set.activeOptionId = set.options[0].id;
        const cmd: Command = {
            id: uuidv4(),
            type: 'ADD_DESIGN_OPTION_SET',
            timestamp: Date.now(),
            description: `Add Design Option Set: ${name}`,
            execute: () => {
                this.project.designOptionSets = [...this.project.designOptionSets, set];
            },
            undo: () => {
                this.project.designOptionSets = this.project.designOptionSets.filter(s => s.id !== set.id);
            }
        };
        this.executeCommand(cmd);
        return set;
    }

    addDesignOption(setId: string, name: string): DesignOption | null {
        const targetSet = this.project.designOptionSets.find(s => s.id === setId);
        if (!targetSet) return null;
        const option: DesignOption = {
            id: uuidv4(),
            name,
            isPrimary: targetSet.options.length === 0,
        };
        const oldSets = this.project.designOptionSets.map(s => ({ ...s, options: [...s.options] }));
        const cmd: Command = {
            id: uuidv4(),
            type: 'ADD_DESIGN_OPTION',
            timestamp: Date.now(),
            description: `Add Design Option: ${name}`,
            execute: () => {
                this.project.designOptionSets = this.project.designOptionSets.map(s =>
                    s.id === setId ? { ...s, options: [...s.options, option] } : s
                );
            },
            undo: () => {
                this.project.designOptionSets = oldSets;
            }
        };
        this.executeCommand(cmd);
        return option;
    }

    removeDesignOption(setId: string, optionId: string): boolean {
        const targetSet = this.project.designOptionSets.find(s => s.id === setId);
        if (!targetSet || targetSet.options.length <= 1) return false;

        const oldSets = this.project.designOptionSets.map(s => ({ ...s, options: [...s.options] }));
        const affected = Array.from(this.project.elements.values())
            .filter(el => el.designOptionId === optionId)
            .map(el => el.id);

        const cmd: Command = {
            id: uuidv4(),
            type: 'REMOVE_DESIGN_OPTION',
            timestamp: Date.now(),
            description: `Remove Design Option from Set: ${targetSet.name}`,
            execute: () => {
                this.project.designOptionSets = this.project.designOptionSets.map(s => {
                    if (s.id !== setId) return s;
                    const options = s.options.filter(o => o.id !== optionId);
                    const activeOptionId = s.activeOptionId === optionId ? options[0]?.id : s.activeOptionId;
                    return { ...s, options, activeOptionId };
                });
                // Orphaned elements return to the Main Model
                const next = new Map(this.project.elements);
                affected.forEach(elId => {
                    const el = next.get(elId);
                    if (el) next.set(elId, { ...el, designOptionId: undefined });
                });
                this.project.elements = next;
            },
            undo: () => {
                this.project.designOptionSets = oldSets;
                const next = new Map(this.project.elements);
                const removed = oldSets.find(s => s.id === setId)?.options.find(o => o.id === optionId);
                affected.forEach(elId => {
                    const el = next.get(elId);
                    if (el && removed) next.set(elId, { ...el, designOptionId: optionId });
                });
                this.project.elements = next;
            }
        };
        this.executeCommand(cmd);
        return true;
    }

    setActiveDesignOption(setId: string, optionId: string): void {
        const targetSet = this.project.designOptionSets.find(s => s.id === setId);
        if (!targetSet || !targetSet.options.some(o => o.id === optionId)) return;
        this.project.designOptionSets = this.project.designOptionSets.map(s =>
            s.id === setId ? { ...s, activeOptionId: optionId } : s
        );
    }

    /** Assign designOptionId on the given elements (Manage > Design Options > Assign to Selection) */
    assignDesignOptionToElements(elementIds: string[], optionId: string | null): void {
        if (elementIds.length === 0) return;

        const oldStates = new Map<string, string | undefined>();
        elementIds.forEach(elId => {
            const el = this.project.elements.get(elId);
            if (el) oldStates.set(elId, el.designOptionId);
        });
        if (oldStates.size === 0) return;

        const cmd: Command = {
            id: uuidv4(),
            type: 'ASSIGN_DESIGN_OPTION',
            timestamp: Date.now(),
            description: optionId
                ? `Assign ${elementIds.length} element(s) to Design Option`
                : `Move ${elementIds.length} element(s) to Main Model`,
            execute: () => {
                const next = new Map(this.project.elements);
                oldStates.forEach((_, elId) => {
                    const el = next.get(elId);
                    if (el) next.set(elId, { ...el, designOptionId: optionId ?? undefined });
                });
                this.project.elements = next;
            },
            undo: () => {
                const next = new Map(this.project.elements);
                oldStates.forEach((oldValue, elId) => {
                    const el = next.get(elId);
                    if (el) next.set(elId, { ...el, designOptionId: oldValue });
                });
                this.project.elements = next;
            }
        };
        this.executeCommand(cmd);
    }

    // ==================== Materials (Manage > Materials) ====================

    /** Resolve a material by id first, then by (case-insensitive) name. */
    getMaterial(key: string | null | undefined): BIMMaterial | undefined {
        if (!key) return undefined;
        const direct = this.project.materials.get(key);
        if (direct) return direct;
        const lower = key.toLowerCase();
        for (const m of this.project.materials.values()) {
            if (m.name.toLowerCase() === lower) return m;
        }
        return undefined;
    }

    getAppearanceAsset(assetId: string | null | undefined): AppearanceAsset | undefined {
        if (!assetId) return undefined;
        return this.project.appearanceAssets.get(assetId);
    }

    /** Create + register a new blank material. Returns the new material id. */
    createMaterial(name: string): string {
        const { material, asset } = createBlankMaterial(name || 'New Material');
        const cmd: Command = {
            id: uuidv4(),
            type: 'CREATE_MATERIAL',
            timestamp: Date.now(),
            description: `Create material ${material.name}`,
            execute: () => {
                this.project.materials.set(material.id, material);
                this.project.appearanceAssets.set(asset.id, asset);
            },
            undo: () => {
                this.project.materials.delete(material.id);
                this.project.appearanceAssets.delete(asset.id);
            },
        };
        this.executeCommand(cmd);
        return material.id;
    }

    /** Patch material sections; appearance changes must go via updateAppearanceAsset. */
    updateMaterial(
        id: string,
        patch: {
            name?: string;
            identity?: Partial<MaterialIdentity>;
            graphics?: Partial<MaterialGraphics>;
            physical?: Partial<MaterialPhysical>;
            thermal?: Partial<MaterialThermal>;
        },
    ): void {
        const material = this.project.materials.get(id);
        if (!material) return;
        const previous: BIMMaterial = structuredClone(material);
        const next: BIMMaterial = {
            ...material,
            name: patch.name !== undefined ? patch.name : material.name,
            identity: { ...material.identity, ...patch.identity },
            graphics: { ...material.graphics, ...patch.graphics },
            physical: { ...material.physical, ...patch.physical },
            thermal: { ...material.thermal, ...patch.thermal },
        };
        const cmd: Command = {
            id: uuidv4(),
            type: 'UPDATE_MATERIAL',
            timestamp: Date.now(),
            description: `Edit ${next.name}`,
            execute: () => { this.project.materials.set(id, next); },
            undo: () => { this.project.materials.set(id, previous); },
        };
        this.executeCommand(cmd);
    }

    /**
     * BIM duplicate semantics:
     *  - duplicateAsset = false → new material SHARES the appearance asset
     *    (editing one appearance edits both — the "linked asset" hand icon).
     *  - duplicateAsset = true → appearance asset is detached into a copy.
     */
    duplicateMaterial(id: string, duplicateAsset: boolean): string | null {
        const source = this.project.materials.get(id);
        if (!source) return null;
        const newId = uuidv4();
        let newAssetId = source.appearanceAssetId;
        const sourceAsset = this.project.appearanceAssets.get(source.appearanceAssetId);
        if (duplicateAsset && sourceAsset) {
            newAssetId = uuidv4();
        }
        const copy: BIMMaterial = {
            ...structuredClone(source),
            id: newId,
            name: this.uniqueMaterialName(`${source.name} (2)`),
            appearanceAssetId: newAssetId,
        };
        const newAsset: AppearanceAsset | undefined = duplicateAsset && sourceAsset
            ? { ...structuredClone(sourceAsset), id: newAssetId, name: copy.name }
            : undefined;
        const cmd: Command = {
            id: uuidv4(),
            type: 'DUPLICATE_MATERIAL',
            timestamp: Date.now(),
            description: `Duplicate ${source.name}`,
            execute: () => {
                if (newAsset) this.project.appearanceAssets.set(newAsset.id, newAsset);
                this.project.materials.set(copy.id, copy);
            },
            undo: () => {
                this.project.materials.delete(copy.id);
                if (newAsset) this.project.appearanceAssets.delete(newAsset.id);
            },
        };
        this.executeCommand(cmd);
        return newId;
    }

    /** Detach a material's shared appearance asset (Duplicate this asset). */
    duplicateAppearanceAsset(materialId: string): void {
        const material = this.project.materials.get(materialId);
        const sourceAsset = this.getAppearanceAsset(material?.appearanceAssetId);
        if (!material || !sourceAsset) return;
        const newAsset: AppearanceAsset = {
            ...structuredClone(sourceAsset),
            id: uuidv4(),
            name: material.name,
        };
        const oldAssetId = material.appearanceAssetId;
        const cmd: Command = {
            id: uuidv4(),
            type: 'DUPLICATE_APPEARANCE_ASSET',
            timestamp: Date.now(),
            description: `Duplicate appearance asset of ${material.name}`,
            execute: () => {
                this.project.appearanceAssets.set(newAsset.id, newAsset);
                const current = this.project.materials.get(materialId);
                if (current) {
                    this.project.materials.set(materialId, { ...current, appearanceAssetId: newAsset.id });
                }
            },
            undo: () => {
                this.project.appearanceAssets.delete(newAsset.id);
                const current = this.project.materials.get(materialId);
                if (current) {
                    this.project.materials.set(materialId, { ...current, appearanceAssetId: oldAssetId });
                }
            },
        };
        this.executeCommand(cmd);
    }

    /** Edit a shared appearance asset — every material referencing it changes. */
    updateAppearanceAsset(assetId: string, patch: Partial<AppearanceAsset>): void {
        const asset = this.project.appearanceAssets.get(assetId);
        if (!asset) return;
        const previous = { ...asset };
        const next = { ...asset, ...patch, id: assetId };
        const cmd: Command = {
            id: uuidv4(),
            type: 'UPDATE_APPEARANCE_ASSET',
            timestamp: Date.now(),
            description: `Edit appearance of ${asset.name}`,
            execute: () => { this.project.appearanceAssets.set(assetId, next); },
            undo: () => { this.project.appearanceAssets.set(assetId, previous); },
        };
        this.executeCommand(cmd);
    }

    /** Element ids that reference a material (by id or name) in any channel. */
    getMaterialUsage(material: BIMMaterial): string[] {
        const usedBy: string[] = [];
        const names = new Set([material.id.toLowerCase(), material.name.toLowerCase()]);
        this.project.elements.forEach(el => {
            const gd: any = (el as any).geometryData;
            const layers: Array<{ materialId?: string }> = Array.isArray(gd?.layers) ? gd.layers : [];
            if (layers.some(l => l.materialId && names.has(l.materialId.toLowerCase()))) {
                usedBy.push(el.id);
                return;
            }
            if ((el as any).structuralMaterial?.name && names.has((el as any).structuralMaterial.name.toLowerCase())) {
                usedBy.push(el.id);
                return;
            }
            for (const val of Object.values(el.parameters ?? {})) {
                if (typeof val === 'string' && names.has(val.toLowerCase())) {
                    usedBy.push(el.id);
                    return;
                }
            }
        });
        return usedBy;
    }

    deleteMaterials(ids: string[]): void {
        const victims = ids
            .map(id => this.project.materials.get(id))
            .filter((m): m is BIMMaterial => !!m);
        if (victims.length === 0) return;
        const assetIdsToRestore: AppearanceAsset[] = [];
        victims.forEach(m => {
            const a = this.project.appearanceAssets.get(m.appearanceAssetId);
            // Only delete the asset when no other material references it.
            if (a && ![...this.project.materials.values()].some(
                other => other.id !== m.id && other.appearanceAssetId === a.id)) {
                assetIdsToRestore.push(structuredClone(a));
            }
        });
        const cmd: Command = {
            id: uuidv4(),
            type: 'DELETE_MATERIALS',
            timestamp: Date.now(),
            description: `Delete ${victims.length} material(s)`,
            execute: () => {
                victims.forEach(m => {
                    this.project.materials.delete(m.id);
                    assetIdsToRestore.forEach(a => {
                        if (a.id === m.appearanceAssetId) this.project.appearanceAssets.delete(a.id);
                    });
                });
            },
            undo: () => {
                victims.forEach(m => this.project.materials.set(m.id, m));
                assetIdsToRestore.forEach(a => this.project.appearanceAssets.set(a.id, a));
            },
        };
        this.executeCommand(cmd);
    }

    /** Manage > Purge Unused: remove materials no element references. */
    purgeUnusedMaterials(): number {
        const unused = [...this.project.materials.values()].filter(
            m => this.getMaterialUsage(m).length === 0);
        if (unused.length > 0) this.deleteMaterials(unused.map(m => m.id));
        return unused.length;
    }

    /**
     * Paint/assign: rewrite materialId on every compound-structure layer of
     * the given elements (Apply to Selection in the browser).
     */
    assignMaterialToElements(elementIds: string[], materialId: string): void {
        const material = this.project.materials.get(materialId);
        if (!material || elementIds.length === 0) return;
        const snapshot = new Map<string, any[]>(); // elementId -> old layers
        elementIds.forEach(elId => {
            const el = this.project.elements.get(elId);
            const layers: any[] = (el as any)?.geometryData?.layers;
            if (el && Array.isArray(layers) && layers.length > 0) {
                snapshot.set(elId, structuredClone(layers));
            }
        });
        if (snapshot.size === 0) return;
        const cmd: Command = {
            id: uuidv4(),
            type: 'ASSIGN_MATERIAL',
            timestamp: Date.now(),
            description: `Apply ${material.name} to ${snapshot.size} element(s)`,
            execute: () => {
                const next = new Map(this.project.elements);
                snapshot.forEach((_, elId) => {
                    const el = next.get(elId);
                    if (!el) return;
                    const gd = (el as any).geometryData;
                    next.set(elId, {
                        ...el,
                        geometryData: {
                            ...gd,
                            layers: gd.layers.map((l: any) => ({ ...l, materialId: material.id })),
                        },
                    });
                });
                this.project.elements = next;
            },
            undo: () => {
                const next = new Map(this.project.elements);
                snapshot.forEach((oldLayers, elId) => {
                    const el = next.get(elId);
                    if (!el) return;
                    next.set(elId, { ...el, geometryData: { ...(el as any).geometryData, layers: oldLayers } });
                });
                this.project.elements = next;
            },
        };
        this.executeCommand(cmd);
    }

    /** Unique name generator for duplicate/rename flows. */
    private uniqueMaterialName(base: string): string {
        const taken = new Set([...this.project.materials.values()].map(m => m.name.toLowerCase()));
        if (!taken.has(base.toLowerCase())) return base;
        let counter = 2;
        const stem = base.replace(/\s*\(\d+\)$/, '');
        while (taken.has(`${stem} (${counter})`.toLowerCase())) counter++;
        return `${stem} (${counter})`;
    }

    /**
     * Transfer Project Standards (Materials): merge an exported material
     * library payload into this document. Colliding names are kept unique by
     * suffixing; ids are always reminted so both documents stay consistent.
     */
    importMaterials(payload: { materials?: BIMMaterial[]; assets?: AppearanceAsset[] }): number {
        const incoming = (payload.materials ?? []).filter(m => m && m.name && m.identity);
        if (incoming.length === 0) return 0;
        const addedMaterials: BIMMaterial[] = [];
        const addedAssets: AppearanceAsset[] = [];
        incoming.forEach(src => {
            const name = this.uniqueMaterialName(src.name);
            const newId = uuidv4();
            let assetId = src.appearanceAssetId;
            const srcAsset = (payload.assets ?? []).find(a => a.id === src.appearanceAssetId);
            if (srcAsset) {
                const newAsset: AppearanceAsset = { ...structuredClone(srcAsset), id: uuidv4(), name };
                assetId = newAsset.id;
                addedAssets.push(newAsset);
            }
            addedMaterials.push({ ...structuredClone(src), id: newId, name, appearanceAssetId: assetId });
        });
        const cmd: Command = {
            id: uuidv4(),
            type: 'IMPORT_MATERIALS',
            timestamp: Date.now(),
            description: `Import ${addedMaterials.length} material(s)`,
            execute: () => {
                addedAssets.forEach(a => this.project.appearanceAssets.set(a.id, a));
                addedMaterials.forEach(m => this.project.materials.set(m.id, m));
            },
            undo: () => {
                addedMaterials.forEach(m => this.project.materials.delete(m.id));
                addedAssets.forEach(a => this.project.appearanceAssets.delete(a.id));
            },
        };
        this.executeCommand(cmd);
        return addedMaterials.length;
    }

    /** Serializable snapshot of the whole material registry (Export). */
    exportMaterials(): { materials: BIMMaterial[]; assets: AppearanceAsset[] } {
        return {
            materials: [...this.project.materials.values()],
            assets: [...this.project.appearanceAssets.values()],
        };
    }

    // --- Data Persistence ---

    /** Structured snapshot of the whole project (maps → entry arrays) — the
     * object saveProject() stringifies. Kept separate so in-memory flows
     * (session tab snapshots) skip the JSON round trip entirely. */
    snapshotProject(): Record<string, unknown> {
        return {
            ...this.project,
            elements: Array.from(this.project.elements.entries()),
            views: Array.from(this.project.views.entries()),
            sheets: Array.from(this.project.sheets.entries()),
            schedules: Array.from(this.project.schedules.entries()),
            families: Array.from(this.project.families.entries()),
            materials: Array.from(this.project.materials.entries()),
            appearanceAssets: Array.from(this.project.appearanceAssets.entries()),
            levels: Array.from(this.project.levels.entries()),
            grids: Array.from(this.project.grids.entries()),
            globalParameters: Array.from(this.project.globalParameters.entries()),
        };
        // customTitleBlocks is a plain array — spread above keeps it intact.
    }

    async saveProject(): Promise<string> {
        // Serialize project to JSON
        const json = JSON.stringify(this.snapshotProject(), null, 2);
        console.log('Project saved:', json.length, 'bytes');
        return json;
    }

    async loadProject(json: string): Promise<void> {
        await this.loadProjectData(JSON.parse(json));
    }

    /** Object entry of loadProject — already-parsed payloads (document load,
     * session tab restore) feed it directly so a load pays ONE parse. */
    loadProjectData(data: any): void {
        // Legacy documents predate the material registry: seed the default
        // library so layer materialIds (by name or legacy catalog id) resolve.
        const hasMaterials = Array.isArray(data.materials) && data.materials.length > 0;
        const seed = hasMaterials ? null : createDefaultMaterialLibrary();
        this.project = {
            ...data,
            customTitleBlocks: data.customTitleBlocks ?? [],
            // Legacy saves store the linked-model category under the old
            // linked-model name — normalize it to "BIM Links" on load.
            elements: new Map(
                (data.elements ?? []).map(([id, el]: [string, any]) =>
                    el?.category === 'Revit Links' ? [id, { ...el, category: 'BIM Links' }] : [id, el],
                ),
            ),
            views: new Map(data.views),
            sheets: new Map(data.sheets),
            schedules: new Map(data.schedules),
            families: new Map(data.families),
            materials: hasMaterials
                ? new Map(data.materials)
                : new Map(seed!.materials.map(m => [m.id, m])),
            appearanceAssets: hasMaterials
                ? new Map(data.appearanceAssets ?? [])
                : new Map(seed!.assets.map(a => [a.id, a])),
            levels: new Map(data.levels),
            grids: new Map(data.grids),
            globalParameters: new Map(data.globalParameters ?? []),
        };
        // Guard: assets referenced by materials but missing (corrupt save) get
        // a neutral replacement so renderers never see an undefined asset.
        for (const material of this.project.materials.values()) {
            if (!this.project.appearanceAssets.has(material.appearanceAssetId)) {
                const asset: AppearanceAsset = {
                    id: material.appearanceAssetId,
                    name: material.name,
                    color: material.graphics.shadingColor || '#CCCCCC',
                    roughness: 0.9,
                    metalness: 0,
                    transparency: 0,
                    imageId: '',
                    tileSize: 1,
                    bumpStrength: 0,
                    emissionColor: '',
                    emissionStrength: 0,
                };
                this.project.appearanceAssets.set(asset.id, asset);
            }
        }
        this.undoStack = [];
        this.redoStack = [];
    }
}

export const bimKernel = new BIMKernel();
